import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEmployees,
  insertEmployee,
  updateEmployee,
  deleteEmployee,
} from "../../../store/empDataSlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../../Styles/Common.css";

const EmployeeManager = () => {
  const dispatch = useDispatch();
  const { employees, loading, error } = useSelector((state) => state.empData);

  // New filter states
  const [firstNameSearch, setFirstNameSearch] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");

  const [showPopup, setShowPopup] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [employeeData, setEmployeeData] = useState({
    id: "",
    empFName: "",
    empLName: "",
    empDept: "Training",
    empCity: "",
    empCountry: "",
    status: "A",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  // Compute unique cities and countries from employees
  const uniqueCities = Array.from(new Set(employees.map(emp => emp.empCity)));
  const uniqueCountries = Array.from(new Set(employees.map(emp => emp.empCountry)));

  // Filter employees based on first name, city, and country
  const filteredEmployees = employees.filter(emp => 
    emp.empFName.toLowerCase().includes(firstNameSearch.toLowerCase()) &&
    (selectedCity ? emp.empCity === selectedCity : true) &&
    (selectedCountry ? emp.empCountry === selectedCountry : true)
  );

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const displayedEmployees = filteredEmployees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleInputChange = (e) => {
    setEmployeeData({ ...employeeData, [e.target.name]: e.target.value });
  };

  const handleStatusToggle = async (employee) => {
    const updatedEmployee = { 
      ...employee, 
      status: employee.status === "A" ? "D" : "A" 
    };
    try {
      await dispatch(updateEmployee(updatedEmployee)).unwrap();
      toast.success(
        `Employee ${updatedEmployee.status === "A" ? "Activated" : "Deactivated"}`
      );
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleInsertOrUpdate = async (e) => {
    e.preventDefault();
    // Validate required fields
    if (
      !employeeData.empFName ||
      !employeeData.empLName ||
      !employeeData.empDept ||
      !employeeData.empCity ||
      !employeeData.empCountry
    ) {
      toast.error("Please fill out all required fields.");
      return;
    }

    try {
      if (editMode) {
        await dispatch(updateEmployee(employeeData)).unwrap();
        toast.success("Employee updated successfully!");
      } else {
        await dispatch(insertEmployee(employeeData)).unwrap();
        toast.success("Employee added successfully!");
      }
      setShowPopup(false);
      setEmployeeData({
        empFName: "",
        empLName: "",
        empDept: "Training",
        empCity: "",
        empCountry: "",
        status: "A",
      });
    } catch {
      toast.error("Failed to save employee.");
    }
  };

  const handleEdit = (employee) => {
    setEmployeeData(employee);
    setEditMode(true);
    setShowPopup(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(`If This Employee is Trainer First Delete From, Trainer Panel. \nAre you Done with it?`)) {
      try {
        await dispatch(deleteEmployee(id)).unwrap();
        toast.success("Employee deleted successfully!");
      } catch {
        toast.error("Failed to delete employee.");
      }
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="employee-container">
      <ToastContainer position="bottom-right" autoClose={3000} />
      <div className="header-container">
        <h2>Employee Management</h2>
        <button
          className="add-lead-btn"
          onClick={() => {
            setShowPopup(true);
            setEditMode(false);
            setEmployeeData({
              empFName: "",
              empLName: "",
              empDept: "Training",
              empCity: "",
              empCountry: "",
              status: "A",
            });
          }}
        >
          <i className="fa fa-plus"></i>
        </button>
      </div>

      {/* New Filters Section */}
      <div className="filters">
        <input
          type="text"
          placeholder="Search by First Name"
          value={firstNameSearch}
          onChange={(e) => setFirstNameSearch(e.target.value)}
          className="filter-input"
        />
        <select
          value={selectedCity}
          onChange={(e) => setSelectedCity(e.target.value)}
          className="filter-input"
        >
          <option value="">All Cities</option>
          {uniqueCities.map((city, index) => (
            <option key={index} value={city}>
              {city}
            </option>
          ))}
        </select>
        <select
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
          className="filter-input"
        >
          <option value="">All Countries</option>
          {uniqueCountries.map((country, index) => (
            <option key={index} value={country}>
              {country}
            </option>
          ))}
        </select>
      </div>

      {/* Employee Table */}
      <table className="table employee-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Emp Id</th>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Department</th>
            <th>City</th>
            <th>Country</th>
            <th>Status</th>
            <th>Created</th>
            <th>Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {displayedEmployees.map((employee, index) => (
            <tr key={employee.id}>
              <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
              <td>{employee.id}</td>
              <td>{employee.empFName}</td>
              <td>{employee.empLName}</td>
              <td>{employee.empDept}</td>
              <td>{employee.empCity}</td>
              <td>{employee.empCountry}</td>
              <td>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={employee.status === "A"}
                    onChange={() => handleStatusToggle(employee)}
                  />
                  <span className="slider round"></span>
                </label>
              </td>
              <td>
                {employee.created
                  ? new Date(employee.created).toLocaleString()
                  : "-"}
              </td>
              <td>
                {employee.updated
                  ? new Date(employee.updated).toLocaleString()
                  : "-"}
              </td>
              <td>
                <button
                  className="icon-btn update-btn fa fa-edit"
                  onClick={() => handleEdit(employee)}
                ></button>
                <button
                  className="icon-btn delete-btn fa fa-trash"
                  onClick={() => handleDelete(employee.id)}
                ></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pagination">
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i + 1}
            onClick={() => handlePageChange(i + 1)}
            className={`pagination-btn ${currentPage === i + 1 ? "active" : ""}`}
          >
            {i + 1}
          </button>
        ))}
      </div>
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{editMode ? "Edit Employee" : "Add Employee"}</h3>
            <form onSubmit={handleInsertOrUpdate} className="form-group">
              <input
                type="text"
                name="empFName"
                placeholder="First Name"
                value={employeeData.empFName}
                onChange={handleInputChange}
                required
              />
              <input
                type="text"
                name="empLName"
                placeholder="Last Name"
                value={employeeData.empLName}
                onChange={handleInputChange}
                required
              />
              <select
                name="empDept"
                value={employeeData.empDept}
                onChange={handleInputChange}
                required
              >
                <option value="Training">Training</option>
                <option value="Sales">Sales</option>
                <option value="HR">HR</option>
                <option value="Admin">Admin</option>
                <option value="Technical">Technical</option>
                <option value="R&D">R&D</option>
                <option value="Marketing">Marketing</option>
                <option value="Others">Others</option>
              </select>
              <input
                type="text"
                name="empCity"
                placeholder="City"
                value={employeeData.empCity}
                onChange={handleInputChange}
                required
              />
              <input
                type="text"
                name="empCountry"
                placeholder="Country"
                value={employeeData.empCountry}
                onChange={handleInputChange}
                required
              />
              <div className="form-actions">
                <button type="submit">
                  {editMode ? (
                    <i className="fa fa-edit"></i>
                  ) : (
                    <i className="fa fa-plus-square"></i>
                  )}
                </button>
                <button type="button" onClick={() => setShowPopup(false)}>
                  <i className="fa fa-close"></i>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManager;
